"use client";
import Link from "next/link";
import {useState, useEffect} from "react";
import {m, useScroll} from "framer-motion";
import ThemeToggle from "@/components/layout/ThemeToggle";
import {Menu, X} from "lucide-react";
import {usePathname} from "next/navigation";

const navLinks = [
  {href: "/#about", label: "ABOUT"},
  {href: "/#projects", label: "PROJECTS"},
  {href: "/#skills", label: "SKILLS"},
  {href: "/#contact", label: "CONTACT"},
];

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const {scrollYProgress} = useScroll();
  const pathname = usePathname();

  useEffect(() => {
    setIsOpen(false);
  }, [pathname]);

  useEffect(() => {
    const unsubscribe = scrollYProgress.on("change", (v) => {
      setScrolled(v > 0.02);
    });
    return () => unsubscribe();
  }, [scrollYProgress]);

  return (
    <header
      className={`fixed top-0 left-0 w-full h-16 flex items-center justify-center z-50 transition-none font-mono ${
        scrolled ? "bg-zinc-950 border-b-2 border-zinc-800" : "bg-transparent"
      }`}
    >
      <div className="max-w-6xl mx-auto px-4 md:px-8 flex items-center justify-between w-full">
        {/* Blinking Prompt Brand */}
        <Link
          href="/"
          className="flex items-center gap-1.5 text-sm font-black uppercase text-zinc-50 hover:text-yellow-400 transition-colors font-display hover-glitch"
          aria-label="Home"
        >
          <span className="text-yellow-400 select-none">[SYS_ACTIVE]</span>
          <span>OLAJIRE_DEV_PORT //</span>
          <m.span
            animate={{opacity: [1, 0]}}
            transition={{duration: 0.8, repeat: Infinity, repeatType: "reverse"}}
            className="inline-block w-1.5 h-3.5 bg-yellow-400"
          />
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-1 text-xs">
          {navLinks.map((link) => {
            const isActive =
              pathname === link.href || pathname.includes(link.href.split("#")[0]);
            return (
              <Link
                key={link.href}
                href={link.href}
                className={`px-3 py-1.5 font-bold border transition-none rounded-none hover-glitch ${
                  isActive
                    ? "bg-zinc-900 text-yellow-400 border-yellow-400"
                    : "bg-transparent text-zinc-400 border-transparent hover:text-zinc-50 hover:border-zinc-800"
                }`}
              >
                {link.label}
              </Link>
            );
          })}
          <div className="ml-3 pl-3 border-l border-zinc-800 flex items-center">
            <ThemeToggle />
          </div>
        </nav>

        {/* Mobile Actions */}
        <div className="md:hidden flex items-center gap-3">
          <ThemeToggle />
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="flex items-center justify-center p-1.5 bg-zinc-950 border-2 border-zinc-800 text-zinc-400 hover:text-yellow-400 hover:border-yellow-400 transition-none cursor-pointer rounded-none hover-glitch"
            aria-label={isOpen ? "Close menu" : "Open menu"}
          >
            {isOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu Popover */}
      {isOpen && (
        <m.div
          initial={{opacity: 0, y: -2}}
          animate={{opacity: 1, y: 0}}
          exit={{opacity: 0, y: -2}}
          transition={{duration: 0.1}}
          className="absolute top-full left-0 right-0 mx-4 mt-2 p-4 bg-zinc-950 border-2 border-zinc-800 shadow-none rounded-none md:hidden z-50"
        >
          <div className="flex flex-col gap-2">
            {navLinks.map((link) => {
              const isActive =
                pathname === link.href || pathname.includes(link.href.split("#")[0]);
              return (
                <Link
                  key={link.href}
                  href={link.href}
                  onClick={() => setIsOpen(false)}
                  className={`block px-4 py-2.5 text-xs font-bold border transition-none rounded-none hover-glitch ${
                    isActive
                      ? "bg-zinc-900 text-yellow-400 border-yellow-400"
                      : "bg-transparent text-zinc-400 border-transparent hover:text-zinc-50 hover:border-zinc-800"
                  }`}
                >
                  &gt; {link.label}
                </Link>
              );
            })}
          </div>
        </m.div>
      )}

      {/* Scroll-Linked Progress bar */}
      <m.div
        className="absolute bottom-0 left-0 h-[2px] bg-yellow-400 origin-left"
        style={{scaleX: scrollYProgress}}
      />
    </header>
  );
}
